/**
 * TopMoverBanner — 홈 §1 '내 보유 카드' 그 자체다(제목·캐러셀 없이 배너 하나, IA SC-019 §1).
 *
 * 이 자리는 카드 미리보기가 아니라 **컬렉션 기능으로 들이는 문**이다.
 * 내가 가진 카드 중 **변동이 가장 큰 한 장**을 집어 "내 것이 지금 움직이고
 * 있다" 를 말한다 — 남의 시세표가 아니라 내 자산이라는 게 이 앱의 USP 이고,
 * 그걸 홈에서 한 문장으로 보여 준다.
 *
 * 오르든 내리든 **절댓값이 가장 큰 카드**를 고른다. 하락도 확인할 이유가 되고,
 * 오른 카드만 고르면 시장이 내려간 날 배너가 통째로 사라진다.
 *
 * ── 닫기(×) ────────────────────────────────────────────────────────────
 * 닫으면 **이번 방문 동안만** 안 보인다. 앱을 떠났다가 돌아오면 다시 뜬다.
 * sessionStorage 를 쓰는 이유가 이것이다 — localStorage 면 한 번 닫은 사람에게
 * 영영 안 보이고, 상태를 안 두면 닫아도 새로고침에 되살아난다.
 *
 * ⚠ 네이티브(Capacitor)에서는 웹뷰가 살아 있는 동안 세션이 이어진다. 앱을
 *   백그라운드로 내린 정도로는 안 돌아오고, 종료 후 재실행하면 돌아온다.
 *   '이탈했다가 회귀' 의 뜻으로 맞다고 보고 이 값을 골랐다.
 *
 * ── 왜 Astryx Banner 가 아닌가 ────────────────────────────────────────
 * 처음에 Banner 로 만들었다가 걷었다. 세 가지가 걸렸다:
 *   · Banner 는 **시스템 메시지**용이다(문서 원문: 오류·점검·시스템 알림).
 *     홍보 배너는 그 쓰임이 아니다.
 *   · 색이 테마 success(#C5E17A 라임)로 고정된다. 같은 화면의 등락 색
 *     (--price-up #6FD79B)과 초록이 두 종류가 된다.
 *   · 머리줄에 CTA 를 넣으면 모바일(327px)에서 글자 칸이 96px 로 짓눌려 제목이
 *     세 줄로 쪼개졌다(실측). 아래 칸으로 내리면 이번엔 접기(^) 화살표가 따라
 *     붙어 닫기(×)와 '숨기는 길' 이 둘이 됐다.
 * ClickableCard 로 두면 **배너 전체가 눌리는 자리**가 된다 — 홍보 배너는
 * 버튼만이 아니라 전체가 눌려야 한다.
 *
 * ⚠ 숫자는 **30일 등락률**이다(PriceView.trendRate). 데이터에 일간 등락이
 *   아직 없다. 그래서 문구에 '오늘' 을 넣지 않았다 — 넣으면 30일치를 하루치라고
 *   말하는 셈이 된다. 일간 값이 생기면 i18n 의 home.moverPeriod 한 줄만 바꾸면
 *   된다 (호출부는 그대로).
 */
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ClickableCard } from "@astryxdesign/core/ClickableCard";
import { Text } from "@astryxdesign/core/Text";
import { VStack, HStack } from "@astryxdesign/core/Layout";
import CardThumb from "../card/CardThumb";
import Icon from "../primitives/Icon";
import { cardImageUrl } from "../../api/card";
import type { HoldingItem } from "../../api/collection";
import { useCollectionQuery } from "../../hooks/useCollectionQueries";
import styles from "./TopMoverBanner.module.css";

/** 이번 방문 동안만 사는 표시. 앱을 떠났다 돌아오면 지워진다. */
const DISMISS_KEY = "peeka.home.moverDismissed";

function loadDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    // 사파리 프라이빗 모드 등에서 sessionStorage 접근이 막힌다 — 그때는 늘 띄운다
    return false;
  }
}

export default function TopMoverBanner() {
  const { t } = useTranslation("card");
  const navigate = useNavigate();
  // 비로그인은 훅이 아예 호출하지 않는다 — 그때는 holdings 가 비어 배너도 안 뜬다.
  const collection = useCollectionQuery();
  const holdings = collection.data?.holdings;
  const [isDismissed, setIsDismissed] = useState(loadDismissed);

  /**
   * 내 보유 중 변동 절댓값이 가장 큰 한 장.
   *
   * ⚠ 등락률은 **그 사람이 가진 배리언트**의 것을 쓴다(HoldingItem.currentPrice).
   *   같은 카드도 감정등급·피니시가 다르면 다른 시장이라 대표가를 쓰면 남의 숫자가 된다.
   */
  const mover = useMemo(() => {
    let best: { holding: HoldingItem; rt: number } | null = null;
    for (const h of holdings ?? []) {
      // currentPrice 는 **그 보유 조합(피니시·패턴·감정등급)** 의 시세라
      // 대표가가 아닌 "내 카드의 등락" 이 된다 — 이 배너가 노리는 값이 그것이다.
      const rt = h.currentPrice.trendRate;
      if (rt === null) continue;
      if (best === null || Math.abs(rt) > Math.abs(best.rt)) best = { holding: h, rt };
    }
    return best;
  }, [holdings]);

  const dismiss = useCallback(() => {
    setIsDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // 저장에 실패해도 이번 화면에서는 닫힌다 — 새로고침하면 돌아온다
    }
  }, []);

  // 보유가 없으면 말할 것이 없다. 0.0% 도 띄우지 않는다 — 움직이지 않은 걸
  // "움직였다" 고 알리면 다음부터 이 배너를 안 믿는다.
  if (isDismissed || mover === null || mover.rt === 0) return null;

  const isUp = mover.rt > 0;
  const pct = Math.abs(mover.rt).toFixed(1);
  const card = mover.holding.card;
  const headline = isUp
    ? t("home.moverUp", { name: card.name, pct })
    : t("home.moverDown", { name: card.name, pct });

  return (
    /* ⚠ 닫기(×)는 ClickableCard **밖**의 형제다. 안에 넣으면 버튼 안의 버튼이라
         눌리는 자리가 겹쳐 어느 쪽이 먹었는지 알 수 없다 (스캔 요약 레일과 같은
         이유·같은 구조). 감싸는 상자에 자리를 준다. */
    <div className={styles.wrap}>
      <ClickableCard label={headline} padding={3} onClick={() => navigate("/collections")}>
        <HStack gap={3} align="center">
          <div className={styles.thumb}>
            <CardThumb imgUrl={cardImageUrl(card.imagePathway) ?? undefined} size="fill" />
          </div>
          <VStack gap={0.5} align="start">
            {/* 등락 방향은 글자색이 아니라 문장이 말한다('올랐어요'/'내렸어요').
                숫자만 색을 입히려면 문장을 토막 내야 하는데, 언어마다 어순이
                달라(일본어는 조사가 붙는다) 토막이 번역을 망가뜨린다. */}
            <Text weight="semibold" maxLines={2}>{headline}</Text>
            <Text type="supporting" color="secondary" maxLines={2}>
              {t("home.moverBody", { period: t("home.moverPeriod") })}
            </Text>
          </VStack>
          {/* 📌 오른쪽 끝 화살표(>)는 2026-08-28 에 걷었다.
              닫기(×)와 **손가락 하나 거리**에 나란히 서 있었다 — 둘 다 24px 이고
              세로로 20px 남짓 떨어져 있어서, 배너를 열려다 닫히고 닫으려다 열렸다.
              화살표가 하던 일(눌리는 자리라는 표시)은 **카드 전체가 이미 하고 있다** —
              ClickableCard 라 어디를 눌러도 컬렉션으로 간다. 그 표시를 위해
              오탭을 만드는 것은 남는 장사가 아니다.
              이제 오른쪽 위 ×만 남아 다른 것과 겹치지 않는다. */}
        </HStack>
      </ClickableCard>

      <button
        type="button"
        className={styles.dismiss}
        onClick={dismiss}
        aria-label={t("home.moverDismiss")}
      >
        <Icon name="Close" size="sm" ariaLabel={t("home.moverDismiss")} />
      </button>
    </div>
  );
}
